import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, ArrowLeft, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      login(data.token, data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel — navy */}
      <div className="relative hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 overflow-hidden">
        <div className="h-1.5 w-full bg-secondary absolute top-0 left-0" />
        <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-secondary/20 blur-3xl" />
        <div className="absolute bottom-0 -left-20 h-72 w-72 rounded-full bg-white/5 blur-3xl" />
        <div className="relative z-10 flex justify-center pt-6">
          <Logo size="xl" chip />
        </div>
        <div className="relative z-10">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold leading-tight">
            {t("login.title")}
          </h1>
          <p className="text-lg text-white/70 mt-4 max-w-md leading-relaxed">{t("landing.hero_sub")}</p>
        </div>
        <p className="relative z-10 text-sm text-white/50">{t("landing.footer")}</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 relative">
        <div className="absolute top-5 right-5"><LanguageToggle /></div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <button onClick={() => navigate("/")} data-testid="login-back-home"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" /> {t("login.back_home")}
          </button>

          <div className="lg:hidden mb-8 flex justify-center"><Logo size="lg" /></div>

          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-primary">{t("login.signin")}</h2>
          <p className="text-muted-foreground mt-2 mb-8">{t("login.subtitle")}</p>

          {error && (
            <div data-testid="login-error-message" className="flex items-start gap-2 rounded-xl bg-destructive/10 text-destructive text-sm p-4 mb-6 border border-destructive/20">
              <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
              <span className="font-medium">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="font-semibold">{t("login.username")}</Label>
              <Input id="username" data-testid="login-username-input" value={username}
                onChange={(e) => setUsername(e.target.value)} placeholder={t("login.username_ph")}
                className="rounded-xl h-14 text-base" autoComplete="username" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-semibold">{t("login.password")}</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder={t("login.password_ph")}
                className="rounded-xl h-14 text-base" autoComplete="current-password" required />
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading}
              className="w-full rounded-full h-14 text-lg font-bold transition-transform hover:-translate-y-0.5 active:translate-y-0 shadow-lg shadow-primary/20">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("login.signin")}
            </Button>
          </form>

          <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-8">
            <Lock className="h-3.5 w-3.5 text-secondary" /> {t("landing.private_note")}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
